import { filterByHealth } from "./filter_health"
import {
  HealthFilterPanel,
  CheckboxFilterPanel,
  FilterTag,
  HealthFilterTag,
} from "./filter_panels"
import { capitalize } from "~/lib/text-helpers"
import { Button } from "~/react/ui/button"
import { Icon } from "~/react/ui/icon"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "~/react/ui/dropdown-menu"
import { useState, useEffect, useRef } from "react"
import { useFilterDropdown } from "~/react/hooks/useFilterDropdown"

export type EmailAccountOption = {
  id: number
  email: string
  status?: "active" | "disconnected"
  owner_name?: string
}

export type DomainFilters = {
  emailAccountIds: number[]
  status: string[]
  healthFrom: number | null
  healthTo: number | null
  healthIsEmpty: boolean
  type: string[]
  searchQuery?: string
}

export type DomainOwner = {
  first_name: string | null
  last_name: string | null
  email: string | null
  company: string | null
  address: string | null
  postal_code: string | null
  city: string | null
  country: string | null
}

export type DomainData = {
  domain: string
  status: { active: number; inactive: number }
  email_accounts_count: number
  email_accounts: string[]
  health: number | null
  type: "connected" | "managed"
  provider: string | null
  redirect_url?: string | null
  expire_at?: string | null
  created_at?: string | null
  owner?: DomainOwner | null
}

type FilterPanel = "email_accounts" | "status" | "health" | "type" | null

const defaultFilters: DomainFilters = {
  emailAccountIds: [],
  status: [],
  healthFrom: null,
  healthTo: null,
  healthIsEmpty: false,
  type: [],
}

export const clearFilters = (): DomainFilters => defaultFilters

const getDomainStatus = (status: {
  active: number
  inactive: number
}): string => {
  if (status.inactive === 0) return "active"
  if (status.active === 0) return "inactive"
  return "mixed"
}

export const filterDomains = (
  data: DomainData[] | null,
  filters: DomainFilters,
  emailAccounts?: EmailAccountOption[],
): DomainData[] | null => {
  if (!data) return null

  let filtered = [...data]

  // Filter by email accounts
  if (filters.emailAccountIds.length > 0 && emailAccounts) {
    // Get domains from selected email accounts
    const selectedEmails = emailAccounts
      .filter((acc) => filters.emailAccountIds.includes(acc.id))
      .map((acc) => acc.email.split("@")[1]?.toLowerCase())
      .filter((domain): domain is string => Boolean(domain))

    filtered =
      selectedEmails.length > 0
        ? filtered.filter((domain) =>
            selectedEmails.includes(domain.domain.toLowerCase()),
          )
        : []
  }

  // Filter by status
  if (filters.status.length > 0) {
    filtered = filtered.filter((domain) =>
      filters.status.includes(getDomainStatus(domain.status)),
    )
  }

  // Filter by health
  filtered = filterByHealth(filtered, {
    from: filters.healthFrom,
    to: filters.healthTo,
    isEmpty: filters.healthIsEmpty,
  })

  // Filter by provider (type)
  if (filters.type.length > 0) {
    filtered = filtered.filter((domain) => {
      // Handle null/empty providers - if filter includes empty string or "null", match domains with null provider
      if (
        (filters.type.includes("") || filters.type.includes("null")) &&
        !domain.provider
      ) {
        return true
      }
      // Match domains with provider that's in the filter list
      return domain.provider !== null && filters.type.includes(domain.provider)
    })
  }

  // Filter by search query
  if (filters.searchQuery?.trim()) {
    const query = filters.searchQuery.trim().toLowerCase()
    filtered = filtered.filter(
      (domain) => domain.domain?.toLowerCase().includes(query) ?? false,
    )
  }

  return filtered
}

type FiltersProps = {
  filters: DomainFilters
  setFilters: (filters: DomainFilters) => void
  emailAccounts?: EmailAccountOption[]
  domains?: DomainData[]
}

const getProviderLabel = (provider: string | null): string => {
  if (!provider)
    return (
      i18n.t("js.email_accounts_center.domains_table.providers.none") || "None"
    )

  const providerMap: Record<string, string> = {
    gmail: i18n.t("js.email_accounts_center.domains_table.providers.gmail"),
    outlook: i18n.t("js.email_accounts_center.domains_table.providers.outlook"),
    smtp_imap: i18n.t(
      "js.email_accounts_center.domains_table.providers.smtp_imap",
    ),
    hubspot: i18n.t("js.email_accounts_center.domains_table.providers.hubspot"),
    zapier: i18n.t("js.email_accounts_center.domains_table.providers.zapier"),
    make: i18n.t("js.email_accounts_center.domains_table.providers.make"),
    clay: i18n.t("js.email_accounts_center.domains_table.providers.clay"),
    zoho: i18n.t("js.email_accounts_center.domains_table.providers.zoho"),
    hunter: i18n.t("js.email_accounts_center.domains_table.providers.hunter"),
    close: i18n.t("js.email_accounts_center.domains_table.providers.close"),
    copper: i18n.t("js.email_accounts_center.domains_table.providers.copper"),
  }

  return (
    providerMap[provider] ||
    provider.charAt(0).toUpperCase() + provider.slice(1)
  )
}

const STATUS_OPTIONS = ["active", "inactive", "mixed"] as const

const getStatusLabel = (status: string): string => {
  const labels: Record<string, string> = {
    active: i18n.t("js.email_accounts_center.domains_table.status.active"),
    inactive: i18n.t("js.email_accounts_center.domains_table.status.inactive"),
    mixed: i18n.t("js.email_accounts_center.domains_table.status.mixed"),
  }
  return labels[status] || status
}

export const FiltersDropdown = ({
  filters,
  setFilters,
  emailAccounts = [],
  domains = [],
}: FiltersProps) => {
  const [activePanel, setActivePanel] = useState<FilterPanel>(null)
  const [tempFilters, setTempFilters] = useState<DomainFilters>(filters)
  const triggerRef = useRef<HTMLButtonElement>(null)

  // Sync tempFilters when filters change externally
  useEffect(() => {
    setTempFilters(filters)
  }, [filters])

  const closeDropdown = () => {
    if (triggerRef.current && (window as any).bootstrap) {
      const dropdownInstance = (window as any).bootstrap.Dropdown.getInstance(
        triggerRef.current,
      )
      dropdownInstance?.hide()
    }
  }

  const applyFilters = () => {
    setFilters(tempFilters)
    setActivePanel(null)
    closeDropdown()
  }

  const openPanel = (panel: NonNullable<FilterPanel>) => {
    setActivePanel(panel)
  }

  const closePanel = () => {
    setActivePanel(null)
  }

  const uniqueProviders = Array.from(
    new Set(
      domains.map((d) => d.provider).filter((p): p is string => p !== null),
    ),
  ).sort()
  const hasNullProvider = domains.some((d) => d.provider === null)

  const renderEmailAccountsPanel = () => (
    <CheckboxFilterPanel
      title={i18n.t(
        "js.email_accounts_center.domains_table.columns.email_accounts",
      )}
      options={emailAccounts.map((acc) => ({
        id: acc.id,
        label: acc.email,
        checked: tempFilters.emailAccountIds.includes(acc.id),
      }))}
      onToggle={(id, checked) =>
        setTempFilters({
          ...tempFilters,
          emailAccountIds: checked
            ? [...tempFilters.emailAccountIds, id as number]
            : tempFilters.emailAccountIds.filter((i) => i !== id),
        })
      }
      onBack={closePanel}
      onApply={applyFilters}
      idPrefix="filter-email-account"
      emptyMessage={i18n.t(
        "js.email_accounts_center.domains_table.email_accounts.empty",
      )}
    />
  )

  const renderStatusPanel = () => (
    <CheckboxFilterPanel
      title={i18n.t("js.email_accounts_center.domains_table.columns.status")}
      options={STATUS_OPTIONS.map((s) => ({
        id: s,
        label: getStatusLabel(s),
        checked: tempFilters.status.includes(s),
      }))}
      onToggle={(id, checked) =>
        setTempFilters({
          ...tempFilters,
          status: checked
            ? [...tempFilters.status, id as string]
            : tempFilters.status.filter((s) => s !== id),
        })
      }
      onBack={closePanel}
      onApply={applyFilters}
      idPrefix="filter-status"
    />
  )

  const renderHealthPanel = () => (
    <HealthFilterPanel
      tempFilters={tempFilters}
      setTempFilters={(h) => setTempFilters({ ...tempFilters, ...h })}
      onBack={closePanel}
      onApply={applyFilters}
      titleKey="js.email_accounts_center.domains_table.columns.health"
    />
  )

  const renderTypePanel = () => {
    const providerOptions = [
      ...(hasNullProvider
        ? [
            {
              id: "",
              label: getProviderLabel(null),
              checked:
                tempFilters.type.includes("") ||
                tempFilters.type.includes("null"),
            },
          ]
        : []),
      ...uniqueProviders.map((p) => ({
        id: p,
        label: getProviderLabel(p),
        checked: tempFilters.type.includes(p),
      })),
    ]

    return (
      <CheckboxFilterPanel
        title={i18n.t("js.email_accounts_center.domains_table.columns.type")}
        options={providerOptions}
        onToggle={(id, checked) => {
          const idStr = id as string
          if (idStr === "" || idStr === "null") {
            setTempFilters({
              ...tempFilters,
              type: checked
                ? [
                    ...tempFilters.type.filter((t) => t !== "" && t !== "null"),
                    "",
                  ]
                : tempFilters.type.filter((t) => t !== "" && t !== "null"),
            })
          } else {
            setTempFilters({
              ...tempFilters,
              type: checked
                ? [...tempFilters.type, idStr]
                : tempFilters.type.filter((t) => t !== idStr),
            })
          }
        }}
        onBack={closePanel}
        onApply={applyFilters}
        idPrefix="filter-provider"
      />
    )
  }

  const filterOptions: {
    panel: NonNullable<FilterPanel>
    label: string
    disabled: boolean
  }[] = [
    {
      panel: "email_accounts",
      label: i18n.t(
        "js.email_accounts_center.domains_table.columns.email_accounts",
      ),
      disabled: filters.emailAccountIds.length > 0,
    },
    {
      panel: "status",
      label: i18n.t("js.email_accounts_center.domains_table.columns.status"),
      disabled: filters.status.length > 0,
    },
    {
      panel: "health",
      label: i18n.t("js.email_accounts_center.domains_table.columns.health"),
      disabled:
        filters.healthFrom !== null ||
        filters.healthTo !== null ||
        filters.healthIsEmpty,
    },
    {
      panel: "type",
      label: i18n.t("js.email_accounts_center.domains_table.columns.type"),
      disabled: filters.type.length > 0,
    },
  ]

  return (
    <DropdownMenu style={{ position: "relative" }}>
      <DropdownMenuTrigger asChild>
        <Button size="sm" ref={triggerRef}>
          <Icon icon="filter" />
          {capitalize(i18n.t("js.filters.label"))}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        {activePanel === null ? (
          <div id="domains-filter-options">
            <ul>
              {filterOptions.map(({ panel, label, disabled }) => (
                <DropdownMenuItem
                  key={panel}
                  disabled={disabled}
                  onClick={() => openPanel(panel)}
                >
                  {label}
                </DropdownMenuItem>
              ))}
            </ul>
          </div>
        ) : (
          <>
            {activePanel === "email_accounts" && renderEmailAccountsPanel()}
            {activePanel === "status" && renderStatusPanel()}
            {activePanel === "health" && renderHealthPanel()}
            {activePanel === "type" && renderTypePanel()}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export const FiltersTags = ({
  filters,
  setFilters,
  emailAccounts = [],
  domains = [],
}: FiltersProps) => {
  const {
    tempFilters,
    setTempFilters,
    openTagDropdown,
    closeTagDropdown,
    isTagDropdownOpen,
  } = useFilterDropdown<NonNullable<FilterPanel>, DomainFilters>(filters)

  const applyFilters = () => {
    setFilters(tempFilters)
    closeTagDropdown()
  }

  const uniqueProviders = Array.from(
    new Set(
      domains.map((d) => d.provider).filter((p): p is string => p !== null),
    ),
  ).sort()
  const hasNullProvider = domains.some((d) => d.provider === null)

  const hasFilters =
    filters.emailAccountIds.length > 0 ||
    filters.status.length > 0 ||
    filters.healthFrom !== null ||
    filters.healthTo !== null ||
    filters.healthIsEmpty ||
    filters.type.length > 0

  if (!hasFilters) return null

  const renderStatusPanel = () => (
    <CheckboxFilterPanel
      title={i18n.t("js.email_accounts_center.domains_table.columns.status")}
      options={STATUS_OPTIONS.map((s) => ({
        id: s,
        label: getStatusLabel(s),
        checked: tempFilters.status.includes(s),
      }))}
      onToggle={(id, checked) =>
        setTempFilters({
          ...tempFilters,
          status: checked
            ? [...tempFilters.status, id as string]
            : tempFilters.status.filter((s) => s !== id),
        })
      }
      onBack={() => closeTagDropdown()}
      onApply={applyFilters}
      idPrefix="filter-status-main"
    />
  )

  const renderHealthPanel = () => (
    <HealthFilterPanel
      tempFilters={tempFilters}
      setTempFilters={(h) => setTempFilters({ ...tempFilters, ...h })}
      onBack={() => closeTagDropdown()}
      onApply={applyFilters}
      idSuffix="main"
      titleKey="js.email_accounts_center.domains_table.columns.health"
    />
  )

  const renderEmailAccountsPanel = () => (
    <CheckboxFilterPanel
      title={i18n.t(
        "js.email_accounts_center.domains_table.columns.email_accounts",
      )}
      options={emailAccounts.map((acc) => ({
        id: acc.id,
        label: acc.email,
        checked: tempFilters.emailAccountIds.includes(acc.id),
      }))}
      onToggle={(id, checked) =>
        setTempFilters({
          ...tempFilters,
          emailAccountIds: checked
            ? [...tempFilters.emailAccountIds, id as number]
            : tempFilters.emailAccountIds.filter((i) => i !== id),
        })
      }
      onBack={() => closeTagDropdown()}
      onApply={applyFilters}
      idPrefix="filter-email-account-main"
      emptyMessage={i18n.t(
        "js.email_accounts_center.domains_table.email_accounts.empty",
      )}
    />
  )

  const renderTypePanel = () => {
    const providerOptions = [
      ...(hasNullProvider
        ? [
            {
              id: "",
              label: getProviderLabel(null),
              checked:
                tempFilters.type.includes("") ||
                tempFilters.type.includes("null"),
            },
          ]
        : []),
      ...uniqueProviders.map((p) => ({
        id: p,
        label: getProviderLabel(p),
        checked: tempFilters.type.includes(p),
      })),
    ]

    return (
      <CheckboxFilterPanel
        title={i18n.t("js.email_accounts_center.domains_table.columns.type")}
        options={providerOptions}
        onToggle={(id, checked) => {
          const idStr = id as string
          if (idStr === "" || idStr === "null") {
            setTempFilters({
              ...tempFilters,
              type: checked
                ? [
                    ...tempFilters.type.filter((t) => t !== "" && t !== "null"),
                    "",
                  ]
                : tempFilters.type.filter((t) => t !== "" && t !== "null"),
            })
          } else {
            setTempFilters({
              ...tempFilters,
              type: checked
                ? [...tempFilters.type, idStr]
                : tempFilters.type.filter((t) => t !== idStr),
            })
          }
        }}
        onBack={() => closeTagDropdown()}
        onApply={applyFilters}
        idPrefix="filter-provider-main"
      />
    )
  }

  return (
    <div className="main-filters mb-4">
      {/* Email accounts filter */}
      {filters.emailAccountIds.length > 0 && emailAccounts.length > 0 && (
        <FilterTag
          label={i18n.t(
            "js.email_accounts_center.domains_table.columns.email_accounts",
          )}
          values={filters.emailAccountIds.map((id) => {
            const acc = emailAccounts.find((a) => a.id === id)
            return { id, label: acc?.email || String(id) }
          })}
          onRemoveValue={(id) =>
            setFilters({
              ...filters,
              emailAccountIds: filters.emailAccountIds.filter((i) => i !== id),
            })
          }
          onClearAll={() => setFilters({ ...filters, emailAccountIds: [] })}
          onOpenDropdown={() => openTagDropdown("email_accounts")}
          isDropdownOpen={isTagDropdownOpen("email_accounts")}
          renderPanel={renderEmailAccountsPanel}
        />
      )}

      {/* Status filter */}
      {filters.status.length > 0 && (
        <FilterTag
          label={i18n.t(
            "js.email_accounts_center.domains_table.columns.status",
          )}
          values={filters.status.map((s) => ({
            id: s,
            label: getStatusLabel(s),
          }))}
          onRemoveValue={(id) =>
            setFilters({
              ...filters,
              status: filters.status.filter((s) => s !== id),
            })
          }
          onClearAll={() => setFilters({ ...filters, status: [] })}
          onOpenDropdown={() => openTagDropdown("status")}
          isDropdownOpen={isTagDropdownOpen("status")}
          renderPanel={renderStatusPanel}
        />
      )}

      {/* Health filter */}
      {(filters.healthFrom !== null ||
        filters.healthTo !== null ||
        filters.healthIsEmpty) && (
        <HealthFilterTag
          filters={filters}
          onClear={() =>
            setFilters({
              ...filters,
              healthFrom: null,
              healthTo: null,
              healthIsEmpty: false,
            })
          }
          onOpenDropdown={() => openTagDropdown("health")}
          isDropdownOpen={isTagDropdownOpen("health")}
          renderPanel={renderHealthPanel}
          labelKey="js.email_accounts_center.domains_table.columns.health"
        />
      )}

      {/* Type filter */}
      {filters.type.length > 0 && (
        <FilterTag
          label={i18n.t("js.email_accounts_center.domains_table.columns.type")}
          values={filters.type.map((p) => ({
            id: p,
            label:
              p === "" || p === "null"
                ? getProviderLabel(null)
                : getProviderLabel(p),
          }))}
          onRemoveValue={(id) =>
            setFilters({
              ...filters,
              type: filters.type.filter((t) => t !== id),
            })
          }
          onClearAll={() => setFilters({ ...filters, type: [] })}
          onOpenDropdown={() => openTagDropdown("type")}
          isDropdownOpen={isTagDropdownOpen("type")}
          renderPanel={renderTypePanel}
        />
      )}
    </div>
  )
}
